/**
 * Generate HTML email template for employee termination notification
 * @param {Object} employeeData - Employee information
 * @param {string} terminationReason - Reason for termination
 * @param {string} terminatedBy - Person who terminated the employee
 * @param {string} terminationDate - Date of termination
 * @param {boolean} isTestEnvironment - Whether this is a test environment
 * @returns {string} - HTML email content
 */
const generateEmployeeTerminationEmailHTML = (employeeData, terminationReason, terminatedBy, terminationDate, isTestEnvironment = false) => {
  const testNote = isTestEnvironment ? `
    <div style="background: #FEF3C7; border: 1px solid #F59E0B; border-radius: 8px; padding: 16px; margin-bottom: 24px;">
      <p style="margin: 0; color: #92400E; font-weight: 600; font-size: 14px;">
        ⚠️ TEST ENVIRONMENT - No action required. This is a test email.
      </p>
    </div>
  ` : '';

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Employee Termination Notification</title>
        <style>
            body {
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                line-height: 1.6;
                color: #374151;
                margin: 0;
                padding: 0;
                background-color: #f9fafb;
            }
            .container {
                max-width: 600px;
                margin: 0 auto;
                background-color: #ffffff;
                border-radius: 12px;
                overflow: hidden;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            }
            .header {
                background: linear-gradient(135deg, #51796F 0%, #3E5D55 100%);
                color: white;
                padding: 32px 24px;
                text-align: center;
            }
            .header h1 {
                margin: 0;
                font-size: 24px;
                font-weight: 700;
            }
            .content {
                padding: 32px 24px;
            }
            .employee-card {
                background: #f8fafc;
                border: 1px solid #e2e8f0;
                border-radius: 12px;
                padding: 24px;
                margin: 24px 0;
            }
            .employee-info {
                display: flex;
                align-items: center;
                gap: 16px;
                margin-bottom: 16px;
            }
            .avatar {
                width: 60px;
                height: 60px;
                border-radius: 50%;
                background: #51796F;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-size: 24px;
                font-weight: 600;
            }
            .employee-details h3 {
                margin: 0 0 8px 0;
                font-size: 20px;
                font-weight: 600;
                color: #1f2937;
            }
            .employee-details p {
                margin: 0;
                color: #6b7280;
                font-size: 14px;
            }
            .termination-details {
                background: #fef2f2;
                border: 1px solid #fecaca;
                border-radius: 8px;
                padding: 20px;
                margin: 20px 0;
            }
            .termination-details h4 {
                margin: 0 0 12px 0;
                color: #dc2626;
                font-size: 16px;
                font-weight: 600;
            }
            .detail-row {
                display: flex;
                justify-content: space-between;
                margin-bottom: 8px;
                padding: 8px 0;
                border-bottom: 1px solid #f3f4f6;
            }
            .detail-row:last-child {
                border-bottom: none;
                margin-bottom: 0;
            }
            .detail-label {
                font-weight: 600;
                color: #374151;
            }
            .detail-value {
                color: #6b7280;
            }
            .reason-box {
                background: #ffffff;
                border: 1px solid #d1d5db;
                border-radius: 6px;
                padding: 12px;
                margin-top: 12px;
                font-style: italic;
                color: #4b5563;
            }
            .footer {
                background: #f9fafb;
                padding: 24px;
                text-align: center;
                border-top: 1px solid #e5e7eb;
            }
            .footer p {
                margin: 0;
                color: #6b7280;
                font-size: 14px;
            }
            .logo {
                width: 40px;
                height: 40px;
                margin: 0 auto 16px;
                background: #51796F;
                border-radius: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-weight: 700;
                font-size: 18px;
            }
            @media (max-width: 600px) {
                .container {
                    margin: 0;
                    border-radius: 0;
                }
                .header, .content, .footer {
                    padding: 24px 16px;
                }
                .employee-info {
                    flex-direction: column;
                    text-align: center;
                }
                .detail-row {
                    flex-direction: column;
                    gap: 4px;
                }
            }
        </style>
    </head>
    <body>
        <div class="container">
            <div class="header">
                <div class="logo">MZ</div>
                <h1>Employee Termination Notification</h1>
            </div>
            
            <div class="content">
                ${testNote}
                
                <p>Dear HR Team,</p>
                
                <p>This is to inform you that an employee has been terminated from the organization. Please find the details below:</p>
                
                <div class="employee-card">
                    <div class="employee-info">
                        <div class="avatar">
                            ${employeeData.name ? employeeData.name.charAt(0).toUpperCase() : 'E'}
                        </div>
                        <div class="employee-details">
                            <h3>${employeeData.name || 'Unknown Employee'}</h3>
                            <p>${employeeData.email || 'N/A'}</p>
                            <p>Employee ID: ${employeeData.empID || 'N/A'}</p>
                        </div>
                    </div>
                </div>
                
                <div class="termination-details">
                    <h4>Termination Details</h4>
                    
                    <div class="detail-row">
                        <span class="detail-label">Employee Name:</span>
                        <span class="detail-value">${employeeData.name || 'Unknown'}</span>
                    </div>
                    
                    <div class="detail-row">
                        <span class="detail-label">Employee ID:</span>
                        <span class="detail-value">${employeeData.empID || 'N/A'}</span>
                    </div>
                    
                    <div class="detail-row">
                        <span class="detail-label">Department:</span>
                        <span class="detail-value">${employeeData.department || 'N/A'}</span>
                    </div>
                    
                    <div class="detail-row">
                        <span class="detail-label">Position:</span>
                        <span class="detail-value">${employeeData.role || 'N/A'}</span>
                    </div>
                    
                    <div class="detail-row">
                        <span class="detail-label">Termination Date:</span>
                        <span class="detail-value">${terminationDate}</span>
                    </div>
                    
                    <div class="detail-row">
                        <span class="detail-label">Terminated By:</span>
                        <span class="detail-value">${terminatedBy}</span>
                    </div>
                    
                    <div class="detail-row">
                        <span class="detail-label">Reason for Termination:</span>
                        <span class="detail-value"></span>
                    </div>
                    
                    <div class="reason-box">
                        "${terminationReason}"
                    </div>
                </div>
                
                <p><strong>Next Steps:</strong></p>
                <ul>
                    <li>Employee authentication account has been deleted</li>
                    <li>Employee access has been revoked from all systems</li>
                    <li>Employee data has been moved to archived records</li>
                    <li>Please ensure all company property is returned</li>
                    <li>Update any relevant documentation and records</li>
                </ul>
                
                <p>If you have any questions regarding this termination, please contact the HR department.</p>
                
                <p>Best regards,<br>
                <strong>MyelinZ HR System</strong></p>
            </div>
            
            <div class="footer">
                <p>&copy; ${new Date().getFullYear()} MyelinZ. All rights reserved.</p>
                <p>This is an automated notification from the MyelinZ HR Management System.</p>
            </div>
        </div>
    </body>
    </html>
  `;
};

export default generateEmployeeTerminationEmailHTML;
